import { useEffect, useState, useRef } from 'react';
import ProjectList from '../components/ProjectList';
import { fetchCategories } from '../lib/categories';
import { ChevronRight, ArrowRight, Camera, Filter } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Realisations() {
  const [categories, setCategories] = useState([]);
  const [catsLoading, setCatsLoading] = useState(false);
  const [selectedFilterId, setSelectedFilterId] = useState(null);
  const [projectsCount, setProjectsCount] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const projectsTopRef = useRef(null);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 900);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    async function loadCats() {
      setCatsLoading(true);
      try {
        const list = await fetchCategories('realisations');
        // On ne garde que les catégories racines pour le filtre premium horizontal
        const rootCats = list?.filter(c => !c.parent) || [];
        setCategories(rootCats);
      } catch (e) {
        console.error(e);
      } finally {
        setCatsLoading(false);
      }
    }
    loadCats();
  }, []);

  const handleFilterClick = (id) => {
    setSelectedFilterId(id);
    if (projectsTopRef.current) {
      window.scrollTo({
        top: projectsTopRef.current.offsetTop - 120,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="realisations-page-signature">
      {/* HEADER SECTION */}
      <section className="realisations-header-premium">
        <div className="container-premium">
          <div className="tag-premium">Showroom AEF 32</div>
          <h1>Nos Réalisations de Prestige</h1>
          <p className="subtitle-premium">
            Découvrez nos dernières installations de portails, clôtures et automatismes.
            Chaque projet est une pièce unique, façonnée pour l'esthétique et la sécurité de votre habitat.
          </p>
        </div>
      </section>

      {/* FILTER BAR SECTION */}
      <section className="filter-bar-premium-container sticky-filter">
        <div className="container-premium">
          <div className="filter-pills-scroll">
            <button
              className={`filter-pill ${selectedFilterId === null ? 'active' : ''}`}
              onClick={() => handleFilterClick(null)}
            >
              Toutes
            </button>
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`filter-pill ${selectedFilterId === cat.id ? 'active' : ''}`}
                onClick={() => handleFilterClick(cat.id)}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="container-premium grid-padding" ref={projectsTopRef}>
        <div className="grid-header-info">
          <div className="count-badge">
            <Camera size={14} />
            <span>{projectsCount} Projet{projectsCount > 1 ? 's' : ''} réalisé{projectsCount > 1 ? 's' : ''}</span>
          </div>
        </div>

        <div className="premium-grid-entrance">
          <ProjectList
            categoryId={selectedFilterId}
            onCountChange={setProjectsCount}
          />
        </div>
      </div>

      {/* CTA SECTION FINAL */}
      <section className="realisations-proximity-section">
        <div className="container-premium">
           <div className="proximity-card-elite shadow-signature">
              <div className="proximity-content-grid">
                 <div className="proximity-text">
                    <h2 className="title-with-accent">Donnez vie à votre projet !</h2>
                    <p className="p-major">
                       Vous avez un projet similaire ? Parlons-en ensemble pour définir la solution idéale pour votre habitat.
                    </p>
                    <div className="proximity-badges-row">
                       <div className="p-badge"><div className="p-dot"></div> Fiabilité</div>
                       <div className="p-badge"><div className="p-dot"></div> Sécurité</div>
                       <div className="p-badge"><div className="p-dot"></div> Durabilité</div>
                    </div>
                    <div className="proximity-actions">
                       <Link to="/contact" className="btn-emerald-signature-full">
                          <span>Demander un devis gratuit</span>
                          <ArrowRight size={20}/>
                       </Link>
                    </div>
                 </div>
                 {!isMobile && (
                   <div className="proximity-icon-box">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="map-icon-luxury"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
                   </div>
                 )}
              </div>
           </div>
        </div>
      </section>

      <style jsx>{`
        .realisations-page-signature { background: #fff; padding-bottom: 0px; min-height: 100vh; font-family: 'Inter', sans-serif; overflow-x: hidden; }
        .container-premium { maxWidth: 1200px; margin: 0 auto; padding: 0 20px; }
        
        .realisations-header-premium { padding: 40px 0 60px 0; background: #fff; text-align: center; }
        .tag-premium { 
           font-size: 11px; font-weight: 800; color: var(--accent); 
           letter-spacing: 2px; margin-bottom: 15px; 
           text-transform: uppercase;
        }
        .realisations-header-premium h1 { 
           font-size: clamp(32px, 5vw, 52px); font-weight: 900; 
           color: #0f172a; margin: 0 0 15px 0; letter-spacing: -1.5px;
        }
        .subtitle-premium { font-size: 18px; color: #64748b; max-width: 800px; margin: 0 auto; line-height: 1.6; }

        .filter-bar-premium-container { background: #fff; border-bottom: 1px solid #f1f5f9; padding: 15px 0; z-index: 100; transition: all 0.3s; }
        .sticky-filter { position: sticky; top: var(--header-height, 0); }
        
        .filter-pills-scroll { display: flex; gap: 10px; overflow-x: auto; padding: 5px 2px; scrollbar-width: none; -ms-overflow-style: none; justify-content: center; }
        .filter-pills-scroll::-webkit-scrollbar { display: none; }
        
        .filter-pill { 
          padding: 10px 24px; border-radius: 99px; border: 1px solid #f1f5f9; background: #fcfcfd; 
          font-size: 14px; font-weight: 700; color: #64748b; cursor: pointer; transition: all 0.3s; white-space: nowrap;
        }
        .filter-pill:hover { border-color: var(--accent); color: var(--accent); background: #fff; }
        .filter-pill.active { background: #0f172a; color: #fff; border-color: #0f172a; box-shadow: 0 10px 20px -5px rgba(15,23,42,0.25); }

        .grid-padding { padding-top: 50px; padding-bottom: 80px; }
        .grid-header-info { margin-bottom: 30px; display: flex; justify-content: flex-end; }
        .count-badge { display: flex; align-items: center; gap: 8px; padding: 6px 14px; background: #fff; border: 1px solid #f1f5f9; border-radius: 99px; font-size: 12px; font-weight: 800; color: #64748b; }

        .premium-grid-entrance { animation: fadeInScale 0.8s cubic-bezier(0.16, 1, 0.3, 1); margin-top: 20px; }
        @keyframes fadeInScale { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }

        /* PROXIMITY CTA */
        .realisations-proximity-section { margin-top: 80px; padding-bottom: 60px; }
        .proximity-card-elite { 
           background: #f0fdf4; border-radius: 36px; border: 1px solid #f1f5f9; padding: 60px;
           position: relative; overflow: hidden; text-align: left;
        }
        .proximity-content-grid { display: grid; grid-template-columns: 1fr 200px; gap: 40px; align-items: center; }
        .title-with-accent { font-size: 28px; font-weight: 950; color: #0f172a; margin-bottom: 25px; position: relative; letter-spacing: -1px; }
        .title-with-accent::after { content: ''; position: absolute; left: 0; bottom: -10px; width: 40px; height: 3px; background: var(--accent); }
        .p-major { font-size: 18px; color: #475569; line-height: 1.6; margin-bottom: 30px; max-width: 650px; }
        .proximity-badges-row { display: flex; gap: 20px; flex-wrap: wrap; margin-bottom: 40px; }
        .p-badge { 
           background: #fff; border: 1px solid #e2e8f0; padding: 8px 16px; border-radius: 12px;
           font-size: 12px; font-weight: 800; color: var(--accent); text-transform: uppercase; letter-spacing: 0.5px;
           display: flex; align-items: center; gap: 8px;
           box-shadow: 0 4px 10px rgba(0,0,0,0.02);
        }
        .p-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--accent); }
        
        .btn-emerald-signature-full {
           background: var(--accent); color: #fff; border: none; padding: 18px 40px; border-radius: 16px; 
           font-weight: 800; display: inline-flex; align-items: center; gap: 10px; cursor: pointer; transition: all 0.3s;
           text-decoration: none; box-shadow: 0 10px 25px rgba(34, 197, 94, 0.25);
        }
        .btn-emerald-signature-full:hover { transform: translateY(-3px); background: var(--accent-600); box-shadow: 0 15px 35px rgba(34, 197, 94, 0.3); }

        .proximity-icon-box { display: flex; justify-content: center; }
        .map-icon-luxury { width: 120px; height: 120px; color: var(--accent); opacity: 0.15; }

        @media (max-width: 900px) {
           .proximity-content-grid { grid-template-columns: 1fr; }
           .proximity-card-elite { padding: 40px 30px; }
           .title-with-accent { font-size: 24px; }
           .btn-emerald-signature-full { width: 100%; justify-content: center; }
        }

        @media (max-width: 768px) {
          .realisations-header-premium { padding: 60px 0 40px 0; }
          .filter-pills-scroll { justify-content: flex-start; padding-left: 20px; padding-right: 20px; margin: 0 -20px; }
        }
      `}</style>
    </div>
  );
}
